/**
 * Security Status Card Component
 *
 * Shows security layer status (L0: PII + Injection detection).
 * Part of the Zero Trust AISecOps demonstration.
 */

import { Shield, ShieldCheck, ShieldAlert, Scan } from 'lucide-react';
import type { StageStatus } from '../../types/pipeline';

interface SecurityStatusCardProps {
  piiStatus: StageStatus;
  guardrailsStatus: StageStatus;
}

export function SecurityStatusCard({
  piiStatus,
  guardrailsStatus,
}: SecurityStatusCardProps) {
  const hasFailed = piiStatus === 'failed' || guardrailsStatus === 'failed';
  const isActive = piiStatus === 'active' || guardrailsStatus === 'active';
  const isComplete =
    piiStatus === 'completed' && guardrailsStatus === 'completed';

  const statusConfig = hasFailed
    ? {
        icon: ShieldAlert,
        bgColor: 'bg-accent-red/10',
        borderColor: 'border-accent-red/30',
        iconColor: 'text-accent-red',
        title: 'Security Check Failed',
        subtitle: 'Potential threat detected',
      }
    : isComplete
      ? {
          icon: ShieldCheck,
          bgColor: 'bg-accent-green/10',
          borderColor: 'border-accent-green/30',
          iconColor: 'text-accent-green',
          title: 'Security Verified',
          subtitle: 'Content sanitized and safe',
        }
      : isActive
        ? {
            icon: Shield,
            bgColor: 'bg-accent-yellow/10',
            borderColor: 'border-accent-yellow/30',
            iconColor: 'text-accent-yellow',
            title: 'Security Scanning',
            subtitle: 'Checking for threats...',
          }
        : {
            icon: Shield,
            bgColor: 'bg-bg-hover',
            borderColor: 'border-border',
            iconColor: 'text-text-muted',
            title: 'Security Pending',
            subtitle: 'Awaiting processing',
          };

  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`rounded-lg border p-4 ${statusConfig.bgColor} ${statusConfig.borderColor}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <StatusIcon className={`w-5 h-5 ${statusConfig.iconColor}`} />
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {statusConfig.title}
          </h3>
          <p className="text-xs text-text-muted">{statusConfig.subtitle}</p>
        </div>
      </div>

      {/* Security checks list */}
      <div className="space-y-2">
        <SecurityCheckRow
          label="PII Detection"
          description="Presidio - Personal data masking"
          status={piiStatus}
        />
        <SecurityCheckRow
          label="Injection Check"
          description="NeMo Guardrails - Prompt injection prevention"
          status={guardrailsStatus}
        />
      </div>

      {/* Failed state details */}
      {hasFailed && (
        <div className="mt-3 p-2 bg-accent-red/10 rounded-sm text-xs text-accent-red">
          <strong>Security Alert:</strong> The content was blocked. This could be
          due to detected PII that couldn't be masked, or a potential prompt
          injection attack.
        </div>
      )}
    </div>
  );
}

interface SecurityCheckRowProps {
  label: string;
  description: string;
  status: StageStatus;
}

function SecurityCheckRow({
  label,
  description,
  status,
}: SecurityCheckRowProps) {
  const statusDisplay = {
    completed: {
      icon: '\u2713',
      color: 'text-accent-green',
      bg: 'bg-accent-green/15',
    },
    active: {
      icon: '\u25CC',
      color: 'text-accent-yellow',
      bg: 'bg-accent-yellow/15',
    },
    pending: {
      icon: '\u25CB',
      color: 'text-text-muted',
      bg: 'bg-bg-hover',
    },
    failed: {
      icon: '\u2717',
      color: 'text-accent-red',
      bg: 'bg-accent-red/15',
    },
    skipped: {
      icon: '\u2298',
      color: 'text-accent-blue',
      bg: 'bg-accent-blue/15',
    },
  }[status];

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <Scan className="w-3.5 h-3.5 text-text-muted" />
        <div>
          <span className="font-medium text-text-primary text-xs">
            {label}
          </span>
          <span className="text-xs text-text-muted ml-2">{description}</span>
        </div>
      </div>
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${statusDisplay.bg} ${statusDisplay.color}`}
      >
        {statusDisplay.icon}
      </span>
    </div>
  );
}
